import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { todayISO } from "../utils/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export default function TireDialog({ vehicleId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ vehicle_id: vehicleId || "", brand: "", dimension: "", quantity: "1", price: "", purchase_date: todayISO(), mount_date: "", mount_mileage: "", position: "", supplier: "", account_id: "" });

  useEffect(() => {
    if (!open) return;
    if (!vehicleId) api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.status !== "Vendu")));
    api.get("/accounts").then((r) => setAccounts(r.data));
    setForm((f) => ({ ...f, vehicle_id: vehicleId || "" }));
  }, [open, vehicleId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.vehicle_id || !form.brand) return toast.error("Véhicule et marque requis");
    try {
      await api.post("/tires", { ...form, price: Number(form.price) || 0, quantity: Number(form.quantity) || 1, mount_mileage: form.mount_mileage ? Number(form.mount_mileage) : null, mount_date: form.mount_date || null, account_id: form.account_id || null });
      toast.success("Pneu(s) enregistré(s) — ajouté(s) au coût du véhicule");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="add-tire-button"><Plus className="mr-1 h-4 w-4" /> Nouveaux pneus</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Achat / montage de pneus</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!vehicleId && (
            <div className="col-span-2"><Label>Véhicule *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                <SelectTrigger data-testid="tire-vehicle-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Marque *</Label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Michelin…" data-testid="tire-brand" /></div>
          <div><Label>Dimension</Label><Input value={form.dimension} onChange={(e) => set("dimension", e.target.value)} placeholder="265/65 R17" data-testid="tire-dimension" /></div>
          <div><Label>Quantité</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></div>
          <div><Label>Prix total (FCFA)</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="tire-price" /></div>
          <div><Label>Date d'achat</Label><Input type="date" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} /></div>
          <div><Label>Date de montage</Label><Input type="date" value={form.mount_date} onChange={(e) => set("mount_date", e.target.value)} /></div>
          <div><Label>Km au montage</Label><Input type="number" value={form.mount_mileage} onChange={(e) => set("mount_mileage", e.target.value)} data-testid="tire-mount-km" /></div>
          <div><Label>Position</Label>
            <Select value={form.position} onValueChange={(v) => set("position", v)}>
              <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{["Avant gauche", "Avant droit", "Arrière gauche", "Arrière droit", "Train avant", "Train arrière", "Les 4", "Roue de secours"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Fournisseur</Label><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} /></div>
          <div><Label>Compte débité</Label>
            <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="tire-submit">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
